import React, { useEffect } from 'react';

import { Flex, useMantineTheme } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { CalendlyButton } from 'components/SmartAssistantComponents/CalendlyButton';
import { ChatConversationView } from 'components/SmartAssistantComponents/ChatConversationView';
import { ChatHeader } from 'components/SmartAssistantComponents/ChatHeader';
import { useChatMessageHandling } from 'hooks/use-chat-message-handling';
import { useSmartAssistantBubbleEventListener } from 'hooks/use-smart-assistant-bubble-event-listener';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { useStatelessSessionStorage } from 'hooks/use-stateless-storage';
import { PosthogService } from 'services/posthog-service';
import { getSessionConversationIdKey } from 'shared/constants/smart-assistant-chat';
import { GuestSmartAssistantData } from 'shared/interfaces/SmartAssistant';

const RECORD_GUEST_SMART_ASSISTANT_SESSIONS = true;

interface GuestTextBasedSmartAssistantPageProps {
  guestSmartAssistantData: GuestSmartAssistantData;
  avatarUrl?: string;
}

export function GuestTextBasedSmartAssistantPage({
  guestSmartAssistantData,
  avatarUrl
}: GuestTextBasedSmartAssistantPageProps) {
  const theme = useMantineTheme();
  const { isVisible, shouldDisableInputZooming } = useSmartAssistantBubbleEventListener();
  const { smartAssistant, conversation } = guestSmartAssistantData;

  const {
    getItem: getSessionConversationId,
    setItem: setSessionConversationId,
    clearItem: resetSessionConversationId
  } = useStatelessSessionStorage({
    key: getSessionConversationIdKey(smartAssistant._id)
  });
  const sessionConversationId = getSessionConversationId();

  const {
    messages,
    sendMessage,
    addNewMessageToConversation,
    isStreamingResponse,
    forceToolInvocation,
    activeConversation,
    setActiveConversationId,
    setNewConversation
  } = useChatMessageHandling({ smartAssistant, type: 'GUEST', initialConversation: conversation });
  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({
    smartAssistant,
    avatarUrl: avatarUrl
  });

  useEffect(() => {
    if (isVisible && smartAssistant && RECORD_GUEST_SMART_ASSISTANT_SESSIONS) {
      PosthogService.init();
      PosthogService.identify(undefined, {
        smartAssistantType: smartAssistant.type,
        smartAssistantId: smartAssistant._id,
        smartAssistantName: smartAssistant.name,
        isGuest: true
      });
    }
  }, [smartAssistant, isVisible]);

  useEffect(() => {
    if (activeConversation.id && !activeConversation.isNew) setSessionConversationId(activeConversation.id);
  }, [activeConversation]);

  useEffect(() => {
    if (sessionConversationId) setActiveConversationId(sessionConversationId);
  }, []);

  const onRefreshClick = () => {
    setNewConversation();
    resetSessionConversationId();
  };

  return (
    <Flex direction="row" w="100vw" h="100svh" style={{ position: 'relative' }}>
      {messages.length > 1 && (
        <IconRefresh
          style={{ cursor: 'pointer', position: 'absolute', top: 20, right: isVisible ? 35 : 10, strokeWidth: 0.7 }}
          onClick={onRefreshClick}
          color={theme.other.getReadableColor(smartAssistantVisualConfig.secondaryBackgroundColor)}
        />
      )}

      <ChatConversationView
        w="100%"
        h="100%"
        messages={messages}
        onSend={sendMessage}
        smartAssistantId={smartAssistant?._id}
        conversationId={activeConversation.id}
        toolConfigs={smartAssistant?.toolConfigs}
        smartAssistantVisualConfig={smartAssistantVisualConfig}
        isStreamingResponse={isStreamingResponse}
        forceToolInvocation={forceToolInvocation}
        header={<ChatHeader smartAssistantVisualConfig={smartAssistantVisualConfig} />}
        displayPoweredBy={!guestSmartAssistantData.removePoweredBy}
        inputTopSection={
          <Flex direction="row" align="center">
            <CalendlyButton
              toolConfigs={smartAssistant?.toolConfigs}
              smartAssistantVisualConfig={smartAssistantVisualConfig}
            />
          </Flex>
        }
        addNewMessageToConversation={addNewMessageToConversation}
        welcomeHeaderProps={{
          avatar: smartAssistantVisualConfig.assistantAvatar,
          title: smartAssistant?.websiteIntegration?.welcomeHeaderTitle,
          subtitle: smartAssistant?.websiteIntegration?.welcomeHeaderSubtitle
        }}
        disableInputZoomingWithinIframe={shouldDisableInputZooming}
        chatAttachmentConfiguration={smartAssistant.chatAttachmentConfiguration}
        isGuest
      />
    </Flex>
  );
}
